/* ==========================================================================
   NADIA — SOPHIE CONRAN CONSUMER CHAT (FLOATING PANEL)
   Version 2.0 — proxy edition
   --------------------------------------------------------------------------
   Talks to the Nadia Worker proxy at /api/nadia. The Anthropic API key lives
   server-side in the Worker; it is NEVER in this file or the browser. The
   system prompt and product knowledge also live server-side (in KV), so this
   embed carries no prompt — only the UI, the signal detection, and the
   streaming reader.

   USAGE — paste this script once into the Shopify theme.
     Optional config (before the script loads):
       <script>
         window.NADIA_CONFIG = {
           proxyUrl: "https://cdn.sophieconran.com/api/nadia", // default
           debug: false,
           signals: { } // optional manual overrides: page, tier, postcode...
         };
       </script>
   No API key is required or accepted here.
   ========================================================================== */

(function() {
  'use strict';

  // ============== CONFIG ==============
  var config = window.NADIA_CONFIG || {};
  var PROXY_URL = config.proxyUrl || 'https://cdn.sophieconran.com/api/nadia';
  // Contact details shown if the live chat can't be reached (ad-blocker, VPN,
  // network filter). Override via window.NADIA_CONFIG. These are the human
  // fallback so a blocked visitor still has a way to reach the team.
  var CONTACT_EMAIL = config.contactEmail || 'hello@sophieconran.com';
  var CONTACT_PHONE = config.contactPhone || '';
  /* Nadia's portrait for the header and nudge avatars. Set via
     window.NADIA_CONFIG.portraitUrl in theme.liquid so the Shopify
     asset_url filter can resolve the CDN path. Falls back to the
     gold "N" monogram if not provided. */
  var PORTRAIT_URL = config.portraitUrl || '';
  var fallbackShown = false; // only show the can't-connect help once per session

  var DEBUG = config.debug === true;

  /* Device class, captured once at load rather than at beacon time so a
     rotation or window resize mid-conversation cannot reclassify it.
     768px is the same breakpoint sc-nadia-bar.css uses to swap the mobile
     bottom bar for the desktop launcher, so "mobile" here means precisely
     "saw the bottom bar" and "desktop" means "saw the launcher bubble".
     Without this, a desktop-only change cannot be separated from one that
     affects both. */
  var DEVICE = (typeof window !== 'undefined' && window.innerWidth <= 768)
    ? 'mobile' : 'desktop';
  var log = function() {
    if (DEBUG) console.log.apply(console, ['[Nadia]'].concat(Array.prototype.slice.call(arguments)));
  };

  /* Avatar markup: Nadia's portrait when configured, gold "N" otherwise.
     Used by the panel header and the launcher nudge bubble. */
  function avatarInner() {
    if (PORTRAIT_URL) {
      return '<img class="nadia-avatar-img" src="' + PORTRAIT_URL +
             '" alt="Nadia" width="64" height="64" loading="eager">';
    }
    return '<em>N</em>';
  }

  // Stable session id so the Worker can upsert the lead across turns. Persists
  // for the browser session (sessionStorage) so a refresh keeps the same lead.
  var SESSION_ID = (function() {
    try {
      var k = 'nadia_session_id';
      var existing = sessionStorage.getItem(k);
      if (existing) return existing;
      var id = (crypto && crypto.randomUUID) ? crypto.randomUUID()
        : ('s-' + Date.now() + '-' + Math.random().toString(36).slice(2));
      sessionStorage.setItem(k, id);
      return id;
    } catch (e) {
      return 's-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    }
  })();

  /* ── Phase 2/3: human-mode relay ──
     When an agent takes over in Slack, the widget polls for their messages
     and sends the visitor's replies via /api/nadia/relay instead of the AI. */
  var humanMode = false;
  var pollTimer = null;
  var POLL_INTERVAL = 3500;
  var POLL_URL = PROXY_URL.replace(/\/api\/nadia$/, '/api/nadia/poll');
  var RELAY_URL = PROXY_URL.replace(/\/api\/nadia$/, '/api/nadia/relay');
  var displayedMsgIds = {}; /* dedup: track displayed message timestamps */

  /* ── Chat persistence across page navigation ──
     Shopify is a multi-page app, so every navigation destroys the widget.
     We save the conversation state to sessionStorage and restore it on init. */
  function saveState() {
    try {
      sessionStorage.setItem('nadia_history', JSON.stringify(conversationHistory));
      sessionStorage.setItem('nadia_started', conversationStarted ? '1' : '');
      sessionStorage.setItem('nadia_human', humanMode ? '1' : '');
      sessionStorage.setItem('nadia_callback_kind', callbackKind || '');
      sessionStorage.setItem('nadia_panel_open', document.getElementById('nadia-panel') && document.getElementById('nadia-panel').classList.contains('open') ? '1' : '');
    } catch (e) {}
  }
  function restoreState() {
    try {
      var saved = sessionStorage.getItem('nadia_history');
      if (!saved) return false;
      var parsed = JSON.parse(saved);
      if (!parsed || !parsed.length) return false;
      conversationHistory = parsed;
      conversationStarted = sessionStorage.getItem('nadia_started') === '1';
      humanMode = sessionStorage.getItem('nadia_human') === '1';
      callbackKind = sessionStorage.getItem('nadia_callback_kind') || '';
      return true;
    } catch (e) { return false; }
  }
  /* The opening turn is a priming instruction sent to the model, not
     something the visitor typed. It has to live in conversationHistory so
     the API sees a valid alternating sequence, but it must never be drawn
     on screen. Without this filter it renders as a visitor bubble when the
     panel restores after a page navigation, which is how a customer ended
     up reading "Read the signals above and deliver your opening greeting
     now" in the chat. */
  function isPrimingMessage(msg) {
    return msg && msg.role === 'user' && typeof msg.content === 'string' &&
      msg.content.indexOf('A new visitor has just arrived on the page') === 0;
  }

  function renderHistory() {
    var container = document.getElementById('nadia-messages');
    if (!container) return;
    for (var i = 0; i < conversationHistory.length; i++) {
      var msg = conversationHistory[i];
      if (isPrimingMessage(msg)) continue;
      if (!msg.content || !msg.content.trim()) continue;
      var el = document.createElement('div');
      el.className = 'nadia-msg ' + (msg.role === 'user' ? 'nadia-msg-user' : 'nadia-msg-nadia');
      if (msg.role === 'user') el.textContent = msg.content;
      else el.innerHTML = formatReply(msg.content);
      container.appendChild(el);
    }
    scrollToBottom();
  }

  /* ── Mobile keyboard ──
     The previous attempt at this sized the panel to the visual viewport
     whenever the keyboard appeared, and applied that to every view. It was
     right for the chat and badly wrong for the gate: a tall form crushed
     into the strip above the keyboard, with the header eating most of what
     was left. The two views want opposite things, so they are handled
     separately here.

     Chat view: shrink to the visible area. The messages scroll and the
     input row sits on top of the keyboard, which is what a chat should do.

     Gate view: leave the panel alone. It is a form taller than the space
     above a keyboard, so shrinking it only hides things. The gate already
     scrolls internally, so the focused field is brought into view instead. */
  /* ── The actual fix for the keyboard, rather than compensating for it ──
     By default iOS leaves the layout viewport alone when the keyboard opens
     and shifts the VISUAL viewport instead. A position: fixed panel is
     anchored to the layout viewport, so it stays where it was and ends up
     above what the visitor can see: the last build left only the input row
     peeking in at the top of the screen.

     interactive-widget=resizes-content tells the browser to resize the
     layout viewport instead. dvh then measures the space above the
     keyboard, fixed elements stay put, and there is nothing left to
     correct. Supported in Safari 17.4+ and Chrome 108+.

     Set only while the panel is open, and restored on close, so the rest
     of the site keeps whatever the theme asked for. On a browser that does
     not support it the attribute is ignored and the JS fallback below
     still runs, so this is safe either way. */
  var originalViewportContent = null;
  function useKeyboardResizingViewport(on) {
    var meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    if (on) {
      if (originalViewportContent === null) {
        originalViewportContent = meta.getAttribute('content') || '';
      }
      if (originalViewportContent.indexOf('interactive-widget') === -1) {
        meta.setAttribute('content',
          originalViewportContent + ', interactive-widget=resizes-content');
      }
    } else if (originalViewportContent !== null) {
      meta.setAttribute('content', originalViewportContent);
      originalViewportContent = null;
    }
  }

  function gateIsShowing() {
    var gate = document.getElementById('nadia-gate');
    return !!(gate && gate.style.display !== 'none');
  }

  var scrollLockY = 0;
  var kbFrame = null;
  function syncKeyboardInset() {
    if (kbFrame) return;
    kbFrame = requestAnimationFrame(function () {
      kbFrame = null;
      var panel = document.getElementById('nadia-panel');
      var vv = window.visualViewport;
      if (!panel) return;

      /* Anything other than a phone-width open panel gets the stylesheet
         back, so a rotation or a resize cannot strand an inline height. */
      if (!vv || window.innerWidth > 480 || !panel.classList.contains('open') || gateIsShowing()) {
        panel.style.height = '';
        panel.style.transform = '';
        return;
      }

      panel.style.height = Math.round(vv.height) + 'px';

      /* Fallback for browsers without interactive-widget support, where the
         layout viewport does not resize and the panel is left above the
         visible area. offsetTop is how far the visual viewport has moved
         down, so translating by it puts the panel back over what is
         actually on screen.

         Where interactive-widget IS supported this is a no-op, because the
         layout viewport resized and offsetTop stays 0. An earlier build
         applied this same translate while the page could still scroll
         underneath, so page scroll and viewport offset compounded and threw
         the panel too far down. The body is pinned now, so offsetTop is the
         only movement left to answer for. */
      var offset = Math.round(vv.offsetTop || 0);
      panel.style.transform = offset > 0 ? 'translateY(' + offset + 'px)' : '';
    });
  }

  /* Bring a focused field into view inside its own scroll container. iOS
     will otherwise scroll the page to do it, and with a fixed-position
     panel that is what produces the lurch. */
  function keepFocusVisible(el) {
    if (!el || window.innerWidth > 480) return;
    setTimeout(function () {
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {
        try { el.scrollIntoView(); } catch (e2) {}
      }
    }, 250);   /* after the keyboard animation, or it scrolls to the wrong place */
  }

  function startPolling() {
    if (pollTimer) return;
    log('Starting agent-message polling');
    pollTimer = setInterval(pollForAgentMessages, POLL_INTERVAL);
    pollForAgentMessages();
  }
  function stopPolling() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  }
  /* Background poll: slower interval, runs after the first conversation exchange
     so agent messages appear immediately even if the visitor hasn't typed again. */
  var bgPollTimer = null;
  var BG_POLL_INTERVAL = 6000;
  function startBackgroundPolling() {
    if (bgPollTimer || pollTimer) return;
    log('background polling started, session=' + SESSION_ID);
    bgPollTimer = setInterval(pollForAgentMessages, BG_POLL_INTERVAL);
  }
  function stopBackgroundPolling() {
    if (bgPollTimer) { clearInterval(bgPollTimer); bgPollTimer = null; }
  }
  function pollForAgentMessages() {
    fetch(POLL_URL + '?session=' + encodeURIComponent(SESSION_ID), { cache: 'no-store' })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        /* Debug: log every poll response so we can trace in the browser console */
        if (data.humanMode || (data.messages && data.messages.length)) {
          log('[poll]', JSON.stringify(data));
        }

        /* Agent typed /done — resume AI mode. */
        if (humanMode && !data.humanMode) {
          humanMode = false;
          stopPolling();
          startBackgroundPolling();
          addSystemMessage('You\'re back with Nadia.');
          return;
        }
        /* Agent replied — switch to human mode if not already. */
        if (data.humanMode && !humanMode) {
          humanMode = true;
          stopBackgroundPolling();
          startPolling();
          addSystemMessage('A team member has joined the conversation.');
        }
        /* Display any queued agent messages. */
        if (data.messages && data.messages.length) {
          var newMsgs = data.messages.filter(function(m) {
            var id = m.ts || m.text; /* dedup by timestamp, fall back to text */
            if (displayedMsgIds[id]) return false;
            displayedMsgIds[id] = true;
            return true;
          });
          if (newMsgs.length) {
            log('[poll] displaying', newMsgs.length, 'agent message(s)');
            for (var i = 0; i < newMsgs.length; i++) addAgentMessage(newMsgs[i].text);
          }
        }
      })
      .catch(function(e) { console.error('[Nadia poll] error:', e); });
  }
  function addAgentMessage(text) {
    var el = document.createElement('div');
    el.className = 'nadia-msg nadia-msg-agent';
    el.textContent = text;
    document.getElementById('nadia-messages').appendChild(el);
    scrollToBottom();
  }
  function addSystemMessage(text) {
    var el = document.createElement('div');
    el.className = 'nadia-msg nadia-msg-system';
    el.textContent = text;
    document.getElementById('nadia-messages').appendChild(el);
    scrollToBottom();
  }
  function sendRelay(text) {
    addUserMessage(text);
    fetch(RELAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session: SESSION_ID,
        text: text,
        page: window.location.pathname
      })
    }).catch(function(e) { log('Relay error:', e); });
  }

  // ============== SIGNAL DETECTION ==============
  function detectSignals() {
    var url = window.location.pathname.toLowerCase();
    var ref = document.referrer.toLowerCase();
    var manual = config.signals || {};

    // Detect page from URL
    var page = manual.page || (function() {
      if (url.includes('cocobella')) return 'cocobella';
      if (url.includes('coco')) return 'coco';
      if (url.includes('barton')) return 'barton';
      if (url.includes('isobel')) return 'isobel';
      if (url.includes('felix')) return 'felix';
      if (url.includes('salthrop') || url.includes('saltrop')) return 'salthrop';
      if (url.includes('henrietta')) return 'henrietta';
      if (url.includes('sophia')) return 'sophia';
      if (url.includes('fabric') || url.includes('sample')) return 'fabric';
      if (url.includes('blog') || url.includes('recipe') || url.includes('article')) return 'recipe';
      if (url.includes('bed-linen') || url.includes('bedding')) return 'bedlinen';
      if (url === '/' || url === '') return 'home';
      return 'other';
    })();

    // Detect source from referrer
    var source = manual.source || (function() {
      if (!ref) return 'direct';
      if (ref.includes('pinterest')) return 'pinterest';
      if (ref.includes('instagram')) return 'instagram';
      if (ref.includes('google') || ref.includes('bing') || ref.includes('duckduckgo')) return 'search';
      if (ref.includes('facebook') || ref.includes('twitter') || ref.includes('tiktok')) return 'social';
      return 'referral';
    })();

    // Returning visitor (uses localStorage as a simple proxy)
    var returning = manual.returning;
    if (returning === undefined) {
      try {
        returning = !!localStorage.getItem('sc_seen_before');
        localStorage.setItem('sc_seen_before', '1');
      } catch (e) {
        returning = false;
      }
    }

    // Time of day
    var hour = new Date().getHours();
    var tod = manual.tod || (
      hour < 6 ? 'latenight' :
      hour < 12 ? 'morning' :
      hour < 17 ? 'afternoon' :
      hour < 22 ? 'evening' : 'latenight'
    );

    // Tier — manual only, requires postcode lookup the preview doesn't have
    var tier = manual.tier || 'unknown';
    var postcode = manual.postcode || '';

    // Device
    var device = window.innerWidth < 768 ? 'mobile' : (window.innerWidth < 1024 ? 'tablet' : 'desktop');

    var signals = {
      page: page,
      source: source,
      tier: tier,
      postcode: postcode,
      returning: returning,
      tod: tod,
      device: device,
      pageUrl: window.location.pathname,
      referrer: document.referrer || 'none',
      dwellStart: Date.now()
    };

    log('Signals detected:', signals);
    return signals;
  }

  function buildSignalContext(signals) {
    var pageNames = {
      home: 'Home page', coco: 'Coco product page', cocobella: 'Cocobella product page',
      barton: 'Barton product page', isobel: 'Isobel product page', felix: 'Felix product page',
      salthrop: 'Salthrop product page', henrietta: 'Henrietta product page',
      sophia: 'Sophia product page', fabric: 'Fabric collection page',
      recipe: 'Recipe / blog page', bedlinen: 'Bed linen page', other: 'Other page'
    };
    var sourceNames = {
      pinterest: 'Pinterest', instagram: 'Instagram', search: 'Google search',
      direct: 'Direct (typed URL)', social: 'Social media', referral: 'External referral'
    };
    var tierNames = {
      ultra_high: 'Ultra High (property £2m+)',
      high: 'High (property £800k–£2m)',
      medium: 'Medium (property £300k–£800k)',
      below: 'Below (under £300k)',
      unknown: 'Unknown (postcode tier not yet established)'
    };

    var dwellSeconds = Math.round((Date.now() - signals.dwellStart) / 1000);

    var lines = [
      '## SIGNALS FOR THIS CONVERSATION',
      '',
      'Live signals read about this visitor as they engaged with the chat:',
      '',
      '- Page: ' + (pageNames[signals.page] || 'Other'),
      '- Source: ' + (sourceNames[signals.source] || 'Unknown'),
      '- Postcode tier: ' + (tierNames[signals.tier] || 'Unknown'),
      '- Returning visitor: ' + (signals.returning ? 'Yes' : 'No (first visit on this device)'),
      '- Time on page before engaging: approximately ' + dwellSeconds + ' seconds',
      '- Time of day: ' + signals.tod,
      '- Device: ' + signals.device
    ];
    if (signals.postcode) lines.push('- Stated postcode: ' + signals.postcode);
    /* Gate-captured contact details: already collected before the chat started */
    try {
      var gateRaw = sessionStorage.getItem('nadia_gate_data');
      if (gateRaw) {
        var g = JSON.parse(gateRaw);
        if (g.name) lines.push('- Name (from pre-chat form): ' + g.name);
        if (g.email) lines.push('- Email (from pre-chat form): ' + g.email);
        if (g.phone) lines.push('- Phone (from pre-chat form): ' + g.phone);
        if (g.countryName) lines.push('- Country (from pre-chat form): ' + g.countryName);
        lines.push('- Contact details are ALREADY CAPTURED. Do not ask for name, email or phone again.');
      }
    } catch (e) {}

    return lines.join('\n');
  }

  // ============== STYLES ==============
  var styles = `
    .nadia-launcher {
      position: fixed; bottom: 24px; right: 24px; z-index: 999999;
      font-family: 'Jost', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 300;
      transition: bottom 0.3s ease;
    }
    /* The sofa configurator's sticky "Add to basket" bar sets this class
       on body and publishes its real height as --sc-sticky-bar-height
       (see setupStickyBar in sc-components.js). Sitting on top of that
       bar rather than stacking above it is the clash this resolves. The
       28px fallback covers the rare case this script's style tag paints
       before that variable exists. */
    body.sc-has-sticky-bar .nadia-launcher {
      bottom: calc(24px + var(--sc-sticky-bar-height, 76px));
    }
    .nadia-bubble {
      background: #1B2D4A; color: #FBF8F1; padding: 14px 20px;
      border-radius: 0; box-shadow: 0 8px 32px rgba(0,0,0,0.18);
      cursor: pointer; max-width: 280px; transition: transform 0.2s;
      display: flex; align-items: center; gap: 12px;
    }
    .nadia-bubble:hover { transform: translateY(-2px); }
    .nadia-bubble-avatar {
      width: 36px; height: 36px; background: #B89A5E; color: #1B2D4A;
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
      font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
      font-weight: 500; font-size: 20px; flex-shrink: 0;
      overflow: hidden; position: relative;
    }
    .nadia-bubble-avatar:has(.nadia-avatar-img) {
      border: 1.5px solid #B89A5E; padding: 1.5px; background: #1B2D4A;
    }
    .nadia-bubble-text {
      font-size: 14px; line-height: 1.4;
    }
    .nadia-bubble-text strong {
      display: block; font-family: 'Cormorant Garamond', Georgia, serif;
      font-style: italic; font-size: 16px; font-weight: 500; margin-bottom: 2px; color: #B89A5E;
    }
    .nadia-bubble-close {
      position: absolute; top: -8px; right: -8px; width: 22px; height: 22px;
      background: #FBF8F1; color: #1B2D4A; border-radius: 50%; cursor: pointer;
      font-size: 14px; line-height: 22px; text-align: center; box-shadow: 0 2px 6px rgba(0,0,0,0.15);
      border: none; padding: 0;
    }

    .nadia-panel {
      position: fixed; bottom: 24px; right: 24px; z-index: 999999;
      width: 380px; height: 580px; max-height: calc(100vh - 48px);
      background: #FBF8F1; box-shadow: 0 12px 48px rgba(0,0,0,0.22);
      display: none; flex-direction: column; overflow: hidden;
      font-family: 'Jost', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-weight: 300;
    }
    .nadia-panel.open { display: flex; }
    .nadia-header {
      background: #1B2D4A; color: #FBF8F1; padding: 16px 20px;
      display: flex; align-items: center; gap: 12px;
    }
    .nadia-header-avatar {
      width: 32px; height: 32px; background: #B89A5E; color: #1B2D4A;
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
      font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
      font-weight: 500; font-size: 18px;
      overflow: hidden; flex-shrink: 0; position: relative;
    }
    /* Nadia's portrait fills the circle; the gold background stays as the
       ring and as the fallback if the image fails to load. */
    .nadia-avatar-img {
      width: 100%; height: 100%; border-radius: 50%;
      object-fit: cover; display: block;
    }
    .nadia-header-avatar:has(.nadia-avatar-img) {
      border: 1.5px solid #B89A5E; padding: 1.5px; background: #1B2D4A;
    }
    .nadia-header-name {
      font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 500; font-size: 18px;
    }
    .nadia-header-status {
      margin-left: auto; font-size: 11px; opacity: 0.7;
      letter-spacing: 0.12em; text-transform: uppercase;
    }
    .nadia-header-status::before { content: "•"; color: #4ade80; margin-right: 6px; font-size: 14px; }
    .nadia-header-close {
      background: transparent; border: none; color: #FBF8F1; font-size: 22px;
      cursor: pointer; padding: 0 0 0 12px; line-height: 1; opacity: 0.7;
    }
    .nadia-header-close:hover { opacity: 1; }
    .nadia-messages {
      flex: 1; overflow-y: auto; padding: 18px 16px; background: #FBF8F1;
    }
    .nadia-msg {
      margin: 10px 0; max-width: 85%; line-height: 1.55; font-size: 14px;
      animation: nadia-fadein 0.3s ease;
    }
    @keyframes nadia-fadein {
      from { opacity: 0; transform: translateY(4px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .nadia-msg-nadia {
      background: #F5EFE3; border-left: 3px solid #B89A5E;
      padding: 10px 14px; color: #2A2A2A;
    }
    .nadia-msg-nadia a {
      color: #1B2D4A; text-decoration: underline; text-underline-offset: 2px;
      word-break: break-word;
    }
    .nadia-msg-nadia a:hover { color: #B89A5E; }
    .nadia-msg-ack {
      font-family: 'Cormorant Garamond', Georgia, serif;
      font-style: italic; color: #7A8B6F;
      background: transparent; border-left: 2px dashed rgba(122, 139, 111, 0.4);
      animation: nadia-ack-pulse 1.4s ease-in-out infinite;
    }
    @keyframes nadia-ack-pulse {
      0%, 100% { opacity: 0.7; }
      50% { opacity: 1; }
    }
    .nadia-msg-user {
      background: #1B2D4A; color: #FBF8F1; padding: 10px 14px; margin-left: auto;
    }
    .nadia-msg-agent {
      background: #2D5A4A; color: #FBF8F1; padding: 10px 14px; margin-right: auto;
    }
    .nadia-msg-system {
      text-align: center; color: #8A8A7A; font-size: 12px; font-style: italic;
      padding: 6px 14px; margin: 4px auto;
    }
    .nadia-msg-error {
      background: rgba(181, 106, 79, 0.1); border-left: 3px solid #B56A4F;
      padding: 10px 14px; color: #B56A4F; font-size: 13px;
    }
    .nadia-msg-help a {
      color: #B56A4F; text-decoration: underline; text-underline-offset: 2px;
    }
    .nadia-msg-help a:hover { color: #1B2D4A; }
    .nadia-typing {
      color: #7A8B6F; font-style: italic; font-size: 13px; padding: 6px 14px; margin: 8px 0;
    }
    .nadia-typing-dot {
      display: inline-block; width: 6px; height: 6px; background: #7A8B6F;
      border-radius: 50%; margin: 0 1px; animation: nadia-dot 1.4s infinite;
    }
    .nadia-typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .nadia-typing-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes nadia-dot {
      0%, 60%, 100% { opacity: 0.3; }
      30% { opacity: 1; }
    }
    .nadia-entry-points {
      padding: 0 16px 10px; display: flex; flex-wrap: wrap; gap: 6px; flex-shrink: 0;
    }
    .nadia-ep-btn {
      background: #EFE7D5; border: none; color: #1B2D4A;
      padding: 7px 12px; font-family: inherit; font-size: 12px; font-weight: 300;
      cursor: pointer; transition: background 0.2s;
    }
    .nadia-ep-btn:hover { background: #B89A5E; color: #FBF8F1; }
    /* The buying-soon button is the highest-intent action in the panel, so
       it is filled rather than outlined and sits first. Same size as the
       others: this is emphasis, not a different kind of control. */
    .nadia-ep-btn.nadia-ep-primary {
      background: #1B2D4A; color: #FBF8F1; border-color: #1B2D4A;
    }
    .nadia-ep-btn.nadia-ep-primary:hover {
      background: #B89A5E; border-color: #B89A5E; color: #1B2D4A;
    }
    .nadia-input-row {
      border-top: 1px solid rgba(27, 45, 74, 0.12); padding: 12px 16px;
      display: flex; gap: 8px; flex-shrink: 0; background: #FBF8F1;
    }
    .nadia-input-row input {
      flex: 1; padding: 9px 12px; border: 1px solid rgba(27, 45, 74, 0.12);
      background: #FBF8F1; font-family: inherit; font-weight: 300;
      font-size: 14px; color: #2A2A2A; outline: none;
    }
    .nadia-input-row input:focus { border-color: #B89A5E; }
    .nadia-input-row button {
      background: #1B2D4A; color: #FBF8F1; border: none; padding: 9px 16px;
      font-family: inherit; font-size: 11px; font-weight: 500;
      letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer;
    }
    .nadia-input-row button:hover { background: #B89A5E; color: #1B2D4A; }
    .nadia-input-row button:disabled { opacity: 0.4; cursor: not-allowed; }

    /* ── Attention-grabbing callout ──
       Sits above the navy launcher bubble, which stays exactly as it was.
       Replaces the former gold "Need a hand?" tag, which was easy to miss
       and said nothing about who Nadia is. Deliberately kept narrower than
       the bubble below it and lighter in weight, so it reads as a spoken
       aside rather than a second interface. */
    .nadia-nudge {
      position: absolute; bottom: calc(100% + 12px); right: 0;
      width: 264px;
      background: #FAF7F0;
      border: 1px solid #B89A5E;
      border-radius: 14px;
      padding: 14px 16px 12px;
      text-align: center;
      box-shadow: 0 8px 22px rgba(27,45,74,0.13);
      cursor: pointer;
      opacity: 0; transform: translateY(8px);
      transition: opacity 0.4s ease, transform 0.4s ease;
      pointer-events: none;
    }
    .nadia-nudge.nadia-nudge-visible {
      opacity: 1; transform: translateY(0); pointer-events: auto;
    }
    /* Rotated square so the gold hairline carries cleanly around the point */
    .nadia-nudge::after {
      content: ''; position: absolute;
      bottom: -7px; right: 28px;
      width: 13px; height: 13px;
      background: #FAF7F0;
      border-right: 1px solid #B89A5E;
      border-bottom: 1px solid #B89A5E;
      border-top: none; border-left: none;
      transform: rotate(45deg);
    }
    .nadia-nudge-eyebrow {
      font-size: 9px; font-weight: 400; letter-spacing: 0.24em;
      text-transform: uppercase; color: #B89A5E; margin-bottom: 6px;
    }
    .nadia-nudge-title {
      font-family: 'Cormorant Garamond', Georgia, serif;
      font-weight: 500; font-size: 19px; line-height: 1.15;
      color: #1B2D4A; margin-bottom: 1px;
    }
    .nadia-nudge-sub {
      font-family: 'Cormorant Garamond', Georgia, serif;
      font-style: italic; font-weight: 400; font-size: 14px;
      color: #1B2D4A; margin-bottom: 8px;
    }
    .nadia-nudge-body {
      font-size: 12px; line-height: 1.55; color: #1B2D4A; opacity: 0.8;
    }
    .nadia-nudge-close {
      position: absolute; top: 7px; right: 9px;
      width: 20px; height: 20px; border: none; background: transparent;
      color: #1B2D4A; opacity: 0.4; font-size: 11px; line-height: 1;
      cursor: pointer; padding: 0; transition: opacity 0.2s ease;
    }
    .nadia-nudge-close:hover { opacity: 0.85; }


    /* ── Lead gate form ── */
    .nadia-gate {
      flex: 1; display: flex; flex-direction: column; justify-content: flex-start;
      padding: 28px 24px; background: #FBF8F1;
      overflow-y: auto; -webkit-overflow-scrolling: touch;
    }
    .nadia-gate-heading {
      font-family: 'Cormorant Garamond', Georgia, serif;
      font-size: 22px; font-weight: 500; color: #1B2D4A;
      margin: 0 0 6px; line-height: 1.3;
    }
    .nadia-gate-sub {
      font-size: 13px; color: #6B6B66; margin: 0 0 20px; line-height: 1.5;
    }
    .nadia-gate-field {
      display: flex; flex-direction: column; margin-bottom: 14px;
    }
    .nadia-gate-field label {
      font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
      color: #1B2D4A; margin-bottom: 5px; font-weight: 400;
    }
    .nadia-gate-field input {
      padding: 10px 12px; border: 1px solid rgba(27,45,74,0.15);
      background: #fff; font-family: inherit; font-weight: 300;
      font-size: 14px; color: #2A2A2A; outline: none;
    }
    .nadia-gate-field input:focus { border-color: #B89A5E; }
    .nadia-gate-field select {
      padding: 10px 12px; border: 1px solid rgba(27,45,74,0.15);
      background: #fff; font-family: inherit; font-weight: 300;
      font-size: 14px; color: #2A2A2A; outline: none;
      appearance: auto;
    }
    .nadia-gate-field select:focus { border-color: #B89A5E; }
    .nadia-gate-field input.nadia-gate-invalid { border-color: #B56A4F; }
    .nadia-gate-btn {
      width: 100%; padding: 12px; background: #1B2D4A; color: #FBF8F1;
      border: none; font-family: inherit; font-size: 12px; font-weight: 500;
      letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer;
      margin-top: 6px; transition: background 0.2s;
    }
    .nadia-gate-btn:hover { background: #B89A5E; color: #1B2D4A; }
    .nadia-gate-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .nadia-gate-skip {
      text-align: center; margin-top: 12px; font-size: 12px; color: #8A8A7A;
    }
    .nadia-gate-skip a {
      color: #8A8A7A; text-decoration: underline; cursor: pointer;
      text-underline-offset: 2px;
    }
    .nadia-gate-skip a:hover { color: #1B2D4A; }

    /* ══ MOBILE ══════════════════════════════════════════════════════════
       The panel was a floating card on mobile: inset 12px from the edges and
       capped at 600px tall, so on a modern phone it covered the bottom two
       thirds and left the site showing above it. Every offset then had to be
       reconciled with the keyboard, which is where the previous attempt at
       this came unstuck. A full-screen sheet removes that arithmetic
       entirely, and is what every mobile chat does anyway.
       ═══════════════════════════════════════════════════════════════════ */
    @media (max-width: 480px) {
      .nadia-panel {
        top: 0; right: 0; bottom: 0; left: 0;
        width: 100%; max-width: none; max-height: none;
        /* 100vh on iOS Safari measures the viewport WITHOUT the address bar
           and toolbar, so a panel sized against it runs underneath them and
           the header is clipped off the top. dvh measures what is actually
           visible. The vh line stays first as a fallback. */
        height: 100vh;
        height: 100dvh;
        border-radius: 0;
      }

      /* Only the middle scrolls. Without this the header can be squeezed by
         the flex algorithm when vertical space is tight, which is how the
         avatar and close button ended up cut off. */
      .nadia-header,
      .nadia-entry-points,
      .nadia-input-row { flex-shrink: 0; }

      /* Keep a scroll gesture inside the panel instead of chaining to the
         page behind it, which is a large part of what reads as jumpiness. */
      .nadia-messages,
      .nadia-gate { overscroll-behavior: contain; }

      /* iOS Safari zooms the whole page in when a focused input has a font
         size below 16px, and every field here was 14px. That zoom is the
         single biggest cause of the lurching on this panel: the page scales
         up on focus and does not scale back. 16px is the threshold, so this
         is the smallest change that stops it rather than a design choice. */
      .nadia-gate-field input,
      .nadia-gate-field select,
      .nadia-input-row input { font-size: 16px; }

      /* Comfortable thumb targets. 44px is Apple's own minimum. */
      .nadia-gate-field input,
      .nadia-gate-field select { min-height: 46px; }
      .nadia-gate-btn { min-height: 50px; }
      .nadia-input-row input { min-height: 44px; }
      .nadia-input-row button { min-height: 44px; padding: 0 18px; }
      .nadia-ep-btn { min-height: 38px; }

      /* Room to breathe at the bottom of a scroll, so the last message or
         the submit button never sits flush against the edge. */
      .nadia-gate { padding-bottom: 40px; }
      .nadia-messages { padding-bottom: 24px; }

      /* With the keyboard up there is very little room left, and the
         suggestion buttons are the least useful thing on screen at that
         moment: the visitor has already decided to type. Hiding them gives
         the conversation two more rows and keeps the last reply in view
         while they answer it. They come straight back on blur. */
      .nadia-panel.nadia-kb-open .nadia-entry-points { display: none; }

      .nadia-launcher { right: 12px; bottom: 12px; }
      .nadia-nudge { width: calc(100vw - 40px); max-width: 290px; }
    }

    /* Hold the page still behind the sheet. Without this the site scrolls
       underneath while the panel is open, which is visible at the edges and
       leaves the visitor somewhere unexpected when they close it. */
    body.nadia-panel-open {
      overflow: hidden;
    }
    /* NOTE: deliberately no touch-action: none on the body. It inherits, so
       it would also kill scrolling inside the panel's own message list and
       gate form, which is far worse than the background moving a little.
       overflow: hidden is enough here, and the panel is position: fixed so
       it is unaffected by it. */
  `;

  // ============== INJECT FONTS + STYLES ==============
  function injectStyles() {
    // Google Fonts (only if not already loaded)
    if (!document.querySelector('link[href*="Cormorant+Garamond"]')) {
      var preconn1 = document.createElement('link');
      preconn1.rel = 'preconnect'; preconn1.href = 'https://fonts.googleapis.com';
      document.head.appendChild(preconn1);
      var preconn2 = document.createElement('link');
      preconn2.rel = 'preconnect'; preconn2.href = 'https://fonts.gstatic.com'; preconn2.crossOrigin = 'anonymous';
      document.head.appendChild(preconn2);
      var fonts = document.createElement('link');
      fonts.rel = 'stylesheet';
      fonts.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400&family=Jost:wght@300;400;500&display=swap';
      document.head.appendChild(fonts);
    }
    var s = document.createElement('style');
    s.textContent = styles;
    document.head.appendChild(s);
  }

  // ============== UI BUILD ==============
  function buildUI() {
    // Launcher (collapsed bubble)
    var launcher = document.createElement('div');
    launcher.className = 'nadia-launcher';
    launcher.id = 'nadia-launcher';
    launcher.innerHTML =
      '<div class="nadia-bubble" id="nadia-bubble">' +
        '<div class="nadia-bubble-avatar">' + avatarInner() + '</div>' +
        '<div class="nadia-bubble-text">' +
          '<strong>I\'m Nadia</strong>' +
          'Here if you need me' +
        '</div>' +
        '<button class="nadia-bubble-close" id="nadia-bubble-close" aria-label="Dismiss">×</button>' +
      '</div>' +
      '<div class="nadia-nudge" id="nadia-nudge">' +
        '<button class="nadia-nudge-close" id="nadia-nudge-close" aria-label="Dismiss">&#10005;</button>' +
        '<p class="nadia-nudge-eyebrow">Here to help</p>' +
        '<p class="nadia-nudge-title">Hello, I\'m Nadia.</p>' +
        '<p class="nadia-nudge-sub">Well, nearly.</p>' +
        '<p class="nadia-nudge-body">She knows every price, fabric and dimension off by heart. So please ask her anything you like x</p>' +
      '</div>';
    document.body.appendChild(launcher);

    // Panel (expanded chat)
    var panel = document.createElement('div');
    panel.className = 'nadia-panel';
    panel.id = 'nadia-panel';
    panel.innerHTML =
      '<div class="nadia-header">' +
        '<div class="nadia-header-avatar">' + avatarInner() + '</div>' +
        '<div class="nadia-header-name">Nadia</div>' +
        '<div class="nadia-header-status">Live</div>' +
        '<button class="nadia-header-close" id="nadia-header-close" aria-label="Close">×</button>' +
      '</div>' +
      /* ── Lead gate: shown first, hidden after submission ── */
      '<div class="nadia-gate" id="nadia-gate">' +
        '<h3 class="nadia-gate-heading">Welcome to Sophie Conran</h3>' +
        '<p class="nadia-gate-sub">Thank you for visiting Sophie Conran, it\'s lovely to have you here. Before we get started, please share your details so that I can help you better and the team can follow up if we get cut off.</p>' +
        '<div class="nadia-gate-field"><label for="nadia-gate-name">Full name</label><input type="text" id="nadia-gate-name" autocomplete="name" placeholder="e.g. Sarah Thompson"></div>' +
        '<div class="nadia-gate-field"><label for="nadia-gate-email">Email</label><input type="email" id="nadia-gate-email" autocomplete="email" placeholder="e.g. sarah@example.com"></div>' +
        '<div class="nadia-gate-field"><label for="nadia-gate-phone">Phone</label><input type="tel" id="nadia-gate-phone" autocomplete="tel" placeholder="e.g. 07700 900000"></div>' +
        '<div class="nadia-gate-field"><label for="nadia-gate-country">Country</label><select id="nadia-gate-country" autocomplete="country"><option value="">Select your country</option><option value="GB">United Kingdom</option><option value="US">United States</option><optgroup label="───────────"><option value="AF">Afghanistan</option><option value="AL">Albania</option><option value="DZ">Algeria</option><option value="AD">Andorra</option><option value="AO">Angola</option><option value="AG">Antigua and Barbuda</option><option value="AR">Argentina</option><option value="AM">Armenia</option><option value="AU">Australia</option><option value="AT">Austria</option><option value="AZ">Azerbaijan</option><option value="BS">Bahamas</option><option value="BH">Bahrain</option><option value="BD">Bangladesh</option><option value="BB">Barbados</option><option value="BY">Belarus</option><option value="BE">Belgium</option><option value="BZ">Belize</option><option value="BJ">Benin</option><option value="BT">Bhutan</option><option value="BO">Bolivia</option><option value="BA">Bosnia and Herzegovina</option><option value="BW">Botswana</option><option value="BR">Brazil</option><option value="BN">Brunei</option><option value="BG">Bulgaria</option><option value="BF">Burkina Faso</option><option value="BI">Burundi</option><option value="KH">Cambodia</option><option value="CM">Cameroon</option><option value="CA">Canada</option><option value="CV">Cape Verde</option><option value="CF">Central African Republic</option><option value="TD">Chad</option><option value="CL">Chile</option><option value="CN">China</option><option value="CO">Colombia</option><option value="KM">Comoros</option><option value="CG">Congo</option><option value="CR">Costa Rica</option><option value="HR">Croatia</option><option value="CU">Cuba</option><option value="CY">Cyprus</option><option value="CZ">Czech Republic</option><option value="DK">Denmark</option><option value="DJ">Djibouti</option><option value="DM">Dominica</option><option value="DO">Dominican Republic</option><option value="EC">Ecuador</option><option value="EG">Egypt</option><option value="SV">El Salvador</option><option value="GQ">Equatorial Guinea</option><option value="ER">Eritrea</option><option value="EE">Estonia</option><option value="ET">Ethiopia</option><option value="FJ">Fiji</option><option value="FI">Finland</option><option value="FR">France</option><option value="GA">Gabon</option><option value="GM">Gambia</option><option value="GE">Georgia</option><option value="DE">Germany</option><option value="GH">Ghana</option><option value="GR">Greece</option><option value="GD">Grenada</option><option value="GT">Guatemala</option><option value="GN">Guinea</option><option value="GW">Guinea-Bissau</option><option value="GY">Guyana</option><option value="HT">Haiti</option><option value="HN">Honduras</option><option value="HU">Hungary</option><option value="IS">Iceland</option><option value="IN">India</option><option value="ID">Indonesia</option><option value="IR">Iran</option><option value="IQ">Iraq</option><option value="IE">Ireland</option><option value="IL">Israel</option><option value="IT">Italy</option><option value="JM">Jamaica</option><option value="JP">Japan</option><option value="JO">Jordan</option><option value="KZ">Kazakhstan</option><option value="KE">Kenya</option><option value="KI">Kiribati</option><option value="KW">Kuwait</option><option value="KG">Kyrgyzstan</option><option value="LA">Laos</option><option value="LV">Latvia</option><option value="LB">Lebanon</option><option value="LS">Lesotho</option><option value="LR">Liberia</option><option value="LY">Libya</option><option value="LI">Liechtenstein</option><option value="LT">Lithuania</option><option value="LU">Luxembourg</option><option value="MG">Madagascar</option><option value="MW">Malawi</option><option value="MY">Malaysia</option><option value="MV">Maldives</option><option value="ML">Mali</option><option value="MT">Malta</option><option value="MH">Marshall Islands</option><option value="MR">Mauritania</option><option value="MU">Mauritius</option><option value="MX">Mexico</option><option value="FM">Micronesia</option><option value="MD">Moldova</option><option value="MC">Monaco</option><option value="MN">Mongolia</option><option value="ME">Montenegro</option><option value="MA">Morocco</option><option value="MZ">Mozambique</option><option value="MM">Myanmar</option><option value="NA">Namibia</option><option value="NR">Nauru</option><option value="NP">Nepal</option><option value="NL">Netherlands</option><option value="NZ">New Zealand</option><option value="NI">Nicaragua</option><option value="NE">Niger</option><option value="NG">Nigeria</option><option value="MK">North Macedonia</option><option value="NO">Norway</option><option value="OM">Oman</option><option value="PK">Pakistan</option><option value="PW">Palau</option><option value="PA">Panama</option><option value="PG">Papua New Guinea</option><option value="PY">Paraguay</option><option value="PE">Peru</option><option value="PH">Philippines</option><option value="PL">Poland</option><option value="PT">Portugal</option><option value="QA">Qatar</option><option value="RO">Romania</option><option value="RU">Russia</option><option value="RW">Rwanda</option><option value="KN">Saint Kitts and Nevis</option><option value="LC">Saint Lucia</option><option value="VC">Saint Vincent</option><option value="WS">Samoa</option><option value="SM">San Marino</option><option value="ST">Sao Tome and Principe</option><option value="SA">Saudi Arabia</option><option value="SN">Senegal</option><option value="RS">Serbia</option><option value="SC">Seychelles</option><option value="SL">Sierra Leone</option><option value="SG">Singapore</option><option value="SK">Slovakia</option><option value="SI">Slovenia</option><option value="SB">Solomon Islands</option><option value="SO">Somalia</option><option value="ZA">South Africa</option><option value="KR">South Korea</option><option value="SS">South Sudan</option><option value="ES">Spain</option><option value="LK">Sri Lanka</option><option value="SD">Sudan</option><option value="SR">Suriname</option><option value="SE">Sweden</option><option value="CH">Switzerland</option><option value="SY">Syria</option><option value="TW">Taiwan</option><option value="TJ">Tajikistan</option><option value="TZ">Tanzania</option><option value="TH">Thailand</option><option value="TL">Timor-Leste</option><option value="TG">Togo</option><option value="TO">Tonga</option><option value="TT">Trinidad and Tobago</option><option value="TN">Tunisia</option><option value="TR">Turkey</option><option value="TM">Turkmenistan</option><option value="TV">Tuvalu</option><option value="UG">Uganda</option><option value="UA">Ukraine</option><option value="AE">United Arab Emirates</option><option value="UY">Uruguay</option><option value="UZ">Uzbekistan</option><option value="VU">Vanuatu</option><option value="VA">Vatican City</option><option value="VE">Venezuela</option><option value="VN">Vietnam</option><option value="YE">Yemen</option><option value="ZM">Zambia</option><option value="ZW">Zimbabwe</option></optgroup></select></div>' +
        '<button class="nadia-gate-btn" id="nadia-gate-submit" disabled>Start chatting</button>' +
      '</div>' +
      /* ── Chat area: hidden until gate is passed ── */
      '<div class="nadia-messages" id="nadia-messages" style="display:none;"></div>' +
      '<div class="nadia-entry-points" id="nadia-entry-points" style="display:none;">' +
        '<button class="nadia-ep-btn nadia-ep-primary" data-ep="buying">I\'m looking to buy soon, call me</button>' +
        '<button class="nadia-ep-btn" data-ep="find">Find my sofa</button>' +
        '<button class="nadia-ep-btn" data-ep="samples">Order free samples</button>' +
        '<button class="nadia-ep-btn" data-ep="catalogue">Get the catalogue</button>' +
        '<button class="nadia-ep-btn" data-ep="ask">Ask a question</button>' +
      '</div>' +
      '<div class="nadia-input-row" id="nadia-input-row" style="display:none;">' +
        '<input type="text" id="nadia-input" placeholder="Type your message..." autocomplete="off">' +
        '<button id="nadia-send">Send</button>' +
      '</div>';
    document.body.appendChild(panel);

    /* ── Gate form: validate + submit ── */
    var gateCompleted = sessionStorage.getItem('nadia_gate_done') === '1';
    if (gateCompleted) {
      unlockChat();
    }

    var gateNameEl = document.getElementById('nadia-gate-name');
    var gateEmailEl = document.getElementById('nadia-gate-email');
    var gatePhoneEl = document.getElementById('nadia-gate-phone');
    var gateSubmitEl = document.getElementById('nadia-gate-submit');

    var gateCountryEl = document.getElementById('nadia-gate-country');
    /* Pre-select country from the Shopify market (set by theme.liquid) */
    var detectedCountry = (window.SC_COUNTRY || '').toUpperCase();
    if (detectedCountry && gateCountryEl.querySelector('option[value="' + detectedCountry + '"]')) {
      gateCountryEl.value = detectedCountry;
    }
    function validateGate() {
      var n = (gateNameEl.value || '').trim();
      var e = (gateEmailEl.value || '').trim();
      var p = (gatePhoneEl.value || '').trim();
      var c = gateCountryEl.value;
      var nameOk = n.length >= 2 && n.indexOf(' ') > 0;
      var emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
      var phoneOk = p.length === 0 || p.replace(/\D/g, '').length >= 9;
      var countryOk = c.length > 0;
      gateNameEl.classList.toggle('nadia-gate-invalid', n.length > 0 && !nameOk);
      gateEmailEl.classList.toggle('nadia-gate-invalid', e.length > 0 && !emailOk);
      gatePhoneEl.classList.toggle('nadia-gate-invalid', p.length > 0 && !phoneOk);
      gateSubmitEl.disabled = !(nameOk && emailOk && phoneOk && countryOk);
    }
    /* iOS Safari fills several fields at once from a saved contact card and
       does not reliably fire an 'input' event when it does. With only an
       'input' listener the submit button stayed disabled on a form that
       looked completely filled in, which reads to the visitor as the chat
       being broken. Listening on change and blur as well catches most of
       it; the interval below catches the rest, because an autofill that
       fires no event at all is not observable any other way. */
    [gateNameEl, gateEmailEl, gatePhoneEl].forEach(function(el) {
      el.addEventListener('input', validateGate);
      el.addEventListener('change', validateGate);
      el.addEventListener('blur', validateGate);
    });

    var gateWatch = setInterval(function () {
      var gate = document.getElementById('nadia-gate');
      /* Stop once the gate is done with, so this does not run for the life
         of the page. */
      if (!gate || gate.style.display === 'none') {
        clearInterval(gateWatch);
        return;
      }
      validateGate();
    }, 400);
    gateCountryEl.addEventListener('change', validateGate);
    gateSubmitEl.addEventListener('click', function() {
      if (gateSubmitEl.disabled) return;
      var gateData = {
        name: gateNameEl.value.trim(),
        email: gateEmailEl.value.trim(),
        phone: gatePhoneEl.value.trim(),
        country: gateCountryEl.value,
        countryName: gateCountryEl.options[gateCountryEl.selectedIndex].text
      };
      sessionStorage.setItem('nadia_gate_done', '1');
      sessionStorage.setItem('nadia_gate_data', JSON.stringify(gateData));
      /* Fire a silent capture_lead via the proxy so the lead is recorded
         immediately, before they even ask their first question. */
      try {
        fetch(PROXY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: SESSION_ID,
            gateCapture: true,
            lead: gateData,
            page: window.location.pathname
          }),
          keepalive: true
        });
      } catch (e) {}
      unlockChat();
    });

    function unlockChat() {
      var gate = document.getElementById('nadia-gate');
      if (gate) gate.style.display = 'none';
      document.getElementById('nadia-messages').style.display = '';
      document.getElementById('nadia-entry-points').style.display = '';
      document.getElementById('nadia-input-row').style.display = '';
      /* The gate is gone, so the keyboard rule for this panel has just
         changed from "leave it alone" to "size it to the visible area". */
      syncKeyboardInset();
    }
  }

  // ============== STATE ==============
  var conversationHistory = [];
  var signals = null;
  var conversationStarted = false;
  /* Set when the visitor uses the "buy soon, call me" split: 'furniture' routes
     the callback to sales (Freshsales), 'other' to support (Gorgias). Sent to
     the worker on every turn while set, and persisted so a page navigation
     mid-flow does not lose it. */
  var callbackKind = '';
  // Guard against overlapping requests. Real users fire several messages in a
  // row ("wait, no", "are you there?"). We never run two requests at once: a
  // message sent while Nadia is replying is queued and delivered, in order,
  // when the current reply finishes.
  var isStreaming = false;
  var pendingMessages = [];

  // ============== API CALL ==============
  // ============== THINKY-QUESTION DETECTION ==============
  function isThinkyQuestion(message) {
    /* Always show the simple typing indicator, never the varied ack messages. */
    return false;
  }

  var ACKNOWLEDGEMENTS = [
    "Let me think how best to explain it…",
    "Hold on a sec, let me think this through…",
    "Bear with me, this is a good one…",
    "Yes, let me think for a moment…",
    "Good question, give me a second…",
    "Right, let me work that through…"
  ];

  function pickAcknowledgement() {
    return ACKNOWLEDGEMENTS[Math.floor(Math.random() * ACKNOWLEDGEMENTS.length)];
  }

  // ============== REPLY FORMATTING ==============
  /* Turn a finished reply into safe display HTML. The model sometimes emits
     markdown the brand voice does not use (bold, headings, bullets) and writes
     URLs the panel showed as dead plain text. This escapes everything first
     (so there is no HTML/script injection surface), strips that markdown to
     plain prose, and turns URLs (bare or [text](url)) into real links. Only
     http/https hrefs are ever produced. Applied once, when a message is
     complete, never mid-stream. */
  function formatReply(text) {
    var s = String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // strip emphasis and inline code, keep the words
    s = s.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/__([^_]+)__/g, '$1').replace(/`([^`]+)`/g, '$1');
    // strip heading hashes and leading bullet markers
    s = s.replace(/^#{1,6}[ \t]+/gm, '').replace(/^[ \t]*[-*][ \t]+/gm, '');
    function anchor(url, label) {
      return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + label + '</a>';
    }
    // hold markdown links so the bare-URL pass below cannot touch their href
    var held = [];
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, function (m, label, url) {
      held.push(anchor(url, label));
      return '' + (held.length - 1) + '';
    });
    // bare http(s) URLs (trailing sentence punctuation kept outside the link)
    s = s.replace(/https?:\/\/[^\s<]+/g, function (u) {
      var tail = ''; var m = u.match(/[.,;:!?)\]]+$/);
      if (m) { tail = m[0]; u = u.slice(0, u.length - tail.length); }
      return anchor(u, u) + tail;
    });
    // bare www. URLs (no scheme)
    s = s.replace(/(^|[\s(])(www\.[^\s<]+)/g, function (m, pre, u) {
      var tail = ''; var t = u.match(/[.,;:!?)\]]+$/);
      if (t) { tail = t[0]; u = u.slice(0, u.length - tail.length); }
      return pre + anchor('https://' + u, u) + tail;
    });
    // restore held markdown-link anchors, then line breaks
    s = s.replace(/(\d+)/g, function (m, i) { return held[+i]; });
    return s.replace(/\n/g, '<br>');
  }

  // ============== TYPING CADENCE ==============
  function createTypingRenderer(msgEl) {
    var buffer = '';
    var rendered = '';
    var renderTimer = null;
    var streamComplete = false;
    var onComplete = null;

    function delayForChar(ch, prevCh) {
      if (rendered.length < 3) return 0;
      if (prevCh === '.' || prevCh === '!' || prevCh === '?') return 180;
      if (prevCh === ',' || prevCh === ';' || prevCh === ':') return 80;
      if (prevCh === '\n') return 200;
      return 14;
    }

    function step() {
      if (rendered.length >= buffer.length) {
        if (streamComplete) {
          renderTimer = null;
          msgEl.innerHTML = formatReply(buffer);
          if (onComplete) onComplete();
          return;
        }
        renderTimer = setTimeout(step, 30);
        return;
      }
      var prevCh = rendered.length > 0 ? rendered[rendered.length - 1] : '';
      var ch = buffer[rendered.length];
      rendered += ch;
      msgEl.textContent = rendered;
      scrollToBottom();
      var delay = delayForChar(ch, prevCh);
      renderTimer = setTimeout(step, delay);
    }

    return {
      addText: function(text) {
        buffer += text;
        if (renderTimer === null) step();
      },
      finish: function(cb) {
        streamComplete = true;
        onComplete = cb || null;
        if (renderTimer === null) step();
      },
      flush: function() {
        if (renderTimer) { clearTimeout(renderTimer); renderTimer = null; }
        rendered = buffer;
        msgEl.innerHTML = formatReply(buffer);
        scrollToBottom();
      }
    };
  }

  function streamMessage(userMessage, isInitial) {
    isStreaming = true;
    var messagesToSend;
    if (isInitial) {
      messagesToSend = [{
        role: 'user',
        content: 'A new visitor has just arrived on the page. Read the signals above and deliver your opening greeting now. Do not introduce yourself with a long preamble, just the natural opening line.'
      }];
    } else if (userMessage) {
      conversationHistory.push({ role: 'user', content: userMessage });
      messagesToSend = conversationHistory.slice();
    } else {
      /* Retry after a silent tool call: re-send the existing history
         without adding a new user message. The worker will see the
         tool-pending record and reconstruct the tool round-trip. */
      messagesToSend = conversationHistory.slice();
    }

    var ackEl = null;
    if (!isInitial && isThinkyQuestion(userMessage)) {
      ackEl = document.createElement('div');
      ackEl.className = 'nadia-msg nadia-msg-nadia nadia-msg-ack';
      ackEl.textContent = pickAcknowledgement();
      document.getElementById('nadia-messages').appendChild(ackEl);
      scrollToBottom();
    } else {
      showTyping();
    }

    var msgEl = null;
    var renderer = null;
    var fullText = '';
    var stopReason = null;
    var desktopRetried = false;


    var mobileRetried = false;
    var isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    var requestBody = {
      sessionId: SESSION_ID,
      signalContext: buildSignalContext(signals),
      messages: messagesToSend,
      page: window.location.pathname
    };
    /* Carries the buy-soon split choice so the worker routes the callback to
       sales or support. Sent on every turn while set, because the callback is
       captured a turn or two after the button press. */
    if (callbackKind) requestBody.callbackKind = callbackKind;

    /* Mobile: read the full SSE response as text (response.text()) instead
       of streaming via ReadableStream. Same worker, same format, just a
       different way of reading that avoids mobile stream truncation.
       The typing animation runs after the full text arrives. */
    if (isMobileDevice) {
      fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(requestBody)
      }).then(function(resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.text();
      }).then(function(raw) {
        hideTyping();
        if (ackEl) { ackEl.remove(); ackEl = null; }
        /* Parse SSE events from the complete response text */
        var text = '';
        var gotHumanMode = false;
        var lines = raw.split('\n');
        for (var li = 0; li < lines.length; li++) {
          var line = lines[li];
          if (!line.startsWith('data: ') && !line.startsWith('data:')) continue;
          var d = line.replace(/^data:\s?/, '').trim();
          if (d === '[DONE]') continue;
          try {
            var evt = JSON.parse(d);
            if (evt.type === 'content_block_delta' && evt.delta && evt.delta.text) {
              text += evt.delta.text;
            }
            if (evt.humanMode) gotHumanMode = true;
          } catch (e) {}
        }
        text = text.trim();
        if (gotHumanMode) {
          humanMode = true;
          sessionStorage.setItem('nadia_human', '1');
        }
        if (text) {
          msgEl = document.createElement('div');
          msgEl.className = 'nadia-msg nadia-msg-nadia';
          document.getElementById('nadia-messages').appendChild(msgEl);
          renderer = createTypingRenderer(msgEl);
          renderer.addText(text);
          fullText = text;
          renderer.finish(function() {
            if (isInitial) {
              conversationHistory.push({
                role: 'user',
                content: 'A new visitor has just arrived on the page. Read the signals above and deliver your opening greeting now. Do not introduce yourself with a long preamble, just the natural opening line.'
              });
            }
            if (fullText && fullText.trim()) {
              conversationHistory.push({ role: 'assistant', content: fullText });
            }
            finishTurn();
          });
        } else {
          /* Empty response = Claude made a silent tool call (e.g. capture_lead).
             The worker stashed the tool state in KV. Retry after a short delay
             so the worker reconstructs the tool history and Claude produces a
             real spoken reply. One retry max to prevent loops. */
          if (!mobileRetried) {
            mobileRetried = true;
            setTimeout(function() {
              streamMessage(null, isInitial);
            }, 900);
          } else {
            fullText = '';
            finishTurn();
          }
        }
      }).catch(function(err) {
        log('Mobile fetch error:', err);
        hideTyping();
        if (ackEl) { ackEl.remove(); ackEl = null; }
        var errEl = document.createElement('div');
        errEl.className = 'nadia-msg nadia-msg-error';
        errEl.textContent = 'Sorry, I had trouble connecting. Please try again.';
        document.getElementById('nadia-messages').appendChild(errEl);
        scrollToBottom();
        finishTurn();
      });
      return;
    }

    fetch(PROXY_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }).then(function(response) {
      if (!response.ok) {
        return response.text().then(function(t) {
          hideTyping();
          if (ackEl) ackEl.remove();
          // Always surface the real status + body in the console (not gated by
          // debug) so failures are diagnosable in the wild. The visible message
          // stays customer-friendly.
          if (window.console && console.warn) {
            console.warn('[Nadia] Proxy error', response.status, t.substring(0, 300));
          }
          var msg = (response.status === 429)
            ? 'Sorry, I\'m at capacity just now. Please try again shortly.'
            : 'Sorry, I\'m having trouble connecting.';
          showError(msg + (DEBUG ? ' (' + response.status + ') ' + t.substring(0, 200) : ''));
          // For a 5xx/0-style failure also offer the human fallback. A 429 is
          // transient (capacity), so we don't show the "blocked" help there.
          if (response.status !== 429) showConnectionHelp();
          var handled = new Error(t);
          handled.nadiaHandled = true;
          throw handled;
        });
      }

      hideTyping();

      /* Phase 2: the Worker returns JSON (not SSE) when the session is in
         human mode. Detect this before trying to read an SSE stream. */
      var ct = response.headers.get('content-type') || '';
      if (ct.indexOf('application/json') !== -1) {
        return response.json().then(function(data) {
          if (data.humanMode) {
            humanMode = true;
            if (ackEl) { ackEl.remove(); ackEl = null; }
            addSystemMessage('Connecting you with someone from the team. They\'ll be right with you.');
            startPolling();
            finishTurn();
          }
        });
      }

      var reader = response.body.getReader();
      var decoder = new TextDecoder();

      function read() {
        return reader.read().then(function(result) {
          if (result.done) {
            var pushAssistant = function() {
              if (isInitial) {
                conversationHistory.push({
                  role: 'user',
                  content: 'A new visitor has just arrived on the page. Read the signals above and deliver your opening greeting now. Do not introduce yourself with a long preamble, just the natural opening line.'
                });
              }
              // Only record a real spoken reply. A turn that was purely a
              // (silent) lead-capture tool call leaves fullText empty; pushing
              // an empty assistant message would break the next request, so we
              // skip it. The lead is already persisted server-side.
              if (fullText && fullText.trim()) {
                conversationHistory.push({ role: 'assistant', content: fullText });
              }
              finishTurn();
            };
            // Empty response = Claude made a silent tool call. The
            // worker stashed the tool state. Retry once after a short
            // delay so the reconstructed history produces a real reply.
            if (!fullText || !fullText.trim()) {
              if (!desktopRetried) {
                desktopRetried = true;
                setTimeout(function() {
                  if (ackEl) { ackEl.remove(); ackEl = null; }
                  if (msgEl && msgEl.parentNode) msgEl.parentNode.removeChild(msgEl);
                  streamMessage(null, isInitial);
                }, 900);
                return;
              }
              fullText = '';
              if (ackEl) { ackEl.remove(); ackEl = null; }
            } else if (stopReason === null && fullText && fullText.trim()) {
              // Stream ended with NO stop_reason — the connection was cut before
              // the model finished (network blip, Worker stream terminated, or
              // extension interference). This is the likely cause of a short
              // mid-word cut-off. Logged so we can confirm if it recurs.
              log('Stream ended with no stop_reason — likely interrupted. length=', fullText.length);
              /* On mobile, flaky connections truncate the stream. Append a
                 subtle tap-to-retry so the visitor isn't stranded. */
              if (msgEl && fullText.length < 60) {
                var retryLink = document.createElement('span');
                retryLink.className = 'nadia-retry-hint';
                retryLink.textContent = ' [message cut short — tap to retry]';
                retryLink.style.cssText = 'color:#B89A5E;cursor:pointer;font-size:12px;font-style:italic;';
                retryLink.addEventListener('click', function() {
                  retryLink.remove();
                  /* Re-send the last user message */
                  var lastUser = '';
                  for (var ri = conversationHistory.length - 1; ri >= 0; ri--) {
                    if (conversationHistory[ri].role === 'user') { lastUser = conversationHistory[ri].content; break; }
                  }
                  if (lastUser) {
                    /* Remove the truncated assistant entry if it was pushed */
                    if (conversationHistory.length && conversationHistory[conversationHistory.length - 1].role === 'assistant') {
                      conversationHistory.pop();
                    }
                    if (msgEl && msgEl.parentNode) msgEl.parentNode.removeChild(msgEl);
                    streamMessage(lastUser, false);
                  }
                });
                msgEl.appendChild(retryLink);
              }
            } else if (stopReason && stopReason !== 'end_turn' && stopReason !== 'tool_use') {
              // Any other non-clean ending (including a genuine length cap on a
              // very long reply). Logged rather than masked, so we always see
              // what actually happened instead of guessing.
              log('Reply ended with stop_reason=', stopReason, 'length=', fullText.length);
            }
            if (renderer) {
              renderer.finish(pushAssistant);
            } else {
              pushAssistant();
            }
            return;
          }
          var chunk = decoder.decode(result.value);
          var lines = chunk.split('\n');
          for (var i = 0; i < lines.length; i++) {
            var line = lines[i];
            if (line.indexOf('data: ') === 0) {
              var data = line.slice(6).trim();
              if (data === '[DONE]') continue;
              try {
                var parsed = JSON.parse(data);
                if (parsed.type === 'content_block_delta' && parsed.delta && parsed.delta.text) {
                  if (!msgEl) {
                    if (ackEl) {
                      ackEl.remove();
                      ackEl = null;
                    }
                    msgEl = document.createElement('div');
                    msgEl.className = 'nadia-msg nadia-msg-nadia';
                    msgEl.textContent = '';
                    document.getElementById('nadia-messages').appendChild(msgEl);
                    renderer = createTypingRenderer(msgEl);
                  }
                  fullText += parsed.delta.text;
                  renderer.addText(parsed.delta.text);
                } else if (parsed.type === 'message_delta' && parsed.delta && parsed.delta.stop_reason) {
                  // Record why the reply ended (end_turn = clean, tool_use =
                  // tool call, max_tokens = length cap). Used for diagnosis.
                  stopReason = parsed.delta.stop_reason;
                }
              } catch (e) { /* skip incomplete chunks */ }
            }
          }
          return read();
        });
      }
      return read();
    }).catch(function(err) {
      hideTyping();
      if (ackEl) ackEl.remove();
      if (renderer) renderer.flush();
      // A thrown fetch that we did NOT already handle above means the request
      // never reached the worker at all — blocked by an extension/VPN/network,
      // or the host is unreachable. This is exactly the case the human fallback
      // is for. (Errors we already surfaced are tagged nadiaHandled.)
      if (!err || !err.nadiaHandled) {
        if (window.console && console.warn) console.warn('[Nadia] Request failed (likely blocked/unreachable):', err && err.message);
        showConnectionHelp();
      }
      finishTurn();
    });
  }

  /* Called once when a turn fully completes (success or error). Releases the
     streaming lock and, if the user queued messages while waiting, sends the
     next one. Folds multiple queued messages into a single turn so a burst of
     impatient messages becomes one natural exchange rather than a backlog. */
  function finishTurn() {
    isStreaming = false;
    saveState(); /* persist across page navigation */
    if (!humanMode) startBackgroundPolling();
    if (pendingMessages.length) {
      var combined = pendingMessages.join('\n\n');
      pendingMessages = [];
      streamMessage(combined, false);
    }
  }

  // ============== UI HELPERS ==============
  function addUserMessage(text) {
    var el = document.createElement('div');
    el.className = 'nadia-msg nadia-msg-user';
    el.textContent = text;
    document.getElementById('nadia-messages').appendChild(el);
    scrollToBottom();
  }
  function showError(text) {
    var el = document.createElement('div');
    el.className = 'nadia-msg nadia-msg-error';
    el.textContent = text;
    document.getElementById('nadia-messages').appendChild(el);
    scrollToBottom();
  }

  /* Shown when the live chat genuinely can't be reached (request blocked by an
     ad-blocker / privacy extension / VPN / network filter, or the proxy is
     unreachable). Gives the visitor a real way to reach the team rather than a
     dead end. Shown once per session to avoid repetition. */
  function showConnectionHelp() {
    if (fallbackShown) return;
    fallbackShown = true;
    var el = document.createElement('div');
    el.className = 'nadia-msg nadia-msg-nadia nadia-msg-help';
    var contactBits = ['<a href="mailto:' + CONTACT_EMAIL + '">' + CONTACT_EMAIL + '</a>'];
    if (CONTACT_PHONE) {
      var telHref = CONTACT_PHONE.replace(/[^0-9+]/g, '');
      contactBits.push('<a href="tel:' + telHref + '">' + CONTACT_PHONE + '</a>');
    }
    el.innerHTML =
      'I can\'t load the live chat just now. This is sometimes caused by an ' +
      'ad-blocker, privacy extension, or a network/VPN setting. ' +
      'You can still reach the team directly at ' + contactBits.join(' or ') + ', ' +
      'and they\'ll be glad to help.';
    document.getElementById('nadia-messages').appendChild(el);
    scrollToBottom();
  }
  function showTyping() {
    hideTyping();
    var t = document.createElement('div');
    t.className = 'nadia-typing';
    t.id = 'nadia-typing';
    t.innerHTML = 'Nadia is typing<span class="nadia-typing-dot"></span><span class="nadia-typing-dot"></span><span class="nadia-typing-dot"></span>';
    document.getElementById('nadia-messages').appendChild(t);
    scrollToBottom();
  }
  function hideTyping() {
    var t = document.getElementById('nadia-typing');
    if (t) t.remove();
  }
  function scrollToBottom() {
    var m = document.getElementById('nadia-messages');
    if (m) m.scrollTop = m.scrollHeight;
  }

  /* ── Attention-grabber nudge ──
     Shows once per session after a short delay. Dismissed by opening the
     panel, closing the launcher, clicking the nudge, its close button, or
     after a timeout. The duration is longer than the 8s the old one-line
     gold tag used, because the card now carries roughly twenty words and
     needs to be readable at a glance without feeling like it is hurrying
     the visitor along. */
  var NUDGE_DELAY = 4000;    /* ms before appearing */
  var NUDGE_DURATION = 15000; /* ms visible */
  var nudgeTimer = null;
  function showNudge() {
    try { if (sessionStorage.getItem('nadia_nudge_seen')) return; } catch (e) {}
    var el = document.getElementById('nadia-nudge');
    if (!el) return;
    nudgeTimer = setTimeout(function () {
      el.classList.add('nadia-nudge-visible');
      nudgeTimer = setTimeout(hideNudge, NUDGE_DURATION);
    }, NUDGE_DELAY);
  }
  function hideNudge() {
    if (nudgeTimer) { clearTimeout(nudgeTimer); nudgeTimer = null; }
    var el = document.getElementById('nadia-nudge');
    if (el) el.classList.remove('nadia-nudge-visible');
    try { sessionStorage.setItem('nadia_nudge_seen', '1'); } catch (e) {}
  }

  // ============== EVENT WIRING ==============
  function openPanel() {
    hideNudge();
    document.getElementById('nadia-launcher').style.display = 'none';
    document.getElementById('nadia-panel').classList.add('open');
    /* Hold the page completely still while the sheet is up.

       overflow: hidden on its own does not stop iOS scrolling, and a page
       that can still scroll is what let the panel ride up when the keyboard
       opened: iOS scrolled the page to reveal the focused field and the
       fixed panel went with it. Pinning the body with position: fixed at a
       negative offset leaves nothing to scroll, so the keyboard cannot move
       anything. The offset is restored on close so the visitor comes back
       to where they were. Mobile only: on desktop the page behind is
       harmless and pinning it would shift the layout. */
    scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add('nadia-panel-open');
    if (window.innerWidth <= 480) {
      useKeyboardResizingViewport(true);
      document.body.style.position = 'fixed';
      document.body.style.top = (-scrollLockY) + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }
    syncKeyboardInset();
    try { document.dispatchEvent(new CustomEvent('nadia:open')); } catch (e) {}
    /* GA4 engagement event, once per browser session */
    try {
      if (!sessionStorage.getItem('nadia_ga4_start')) {
        sessionStorage.setItem('nadia_ga4_start', '1');
        if (typeof gtag === 'function') gtag('event', 'nadia_chat_start');
      }
    } catch (e) {}
    if (!conversationStarted) {
      /* Try restoring a previous conversation from this browser session. */
      if (restoreState()) {
        renderHistory();
        if (humanMode) startPolling();
        else startBackgroundPolling();
      } else {
        conversationStarted = true;
        streamMessage('', true);
      }
    }
  }
  /* ── Conversation capture ──
     Beacon the full transcript to the Worker whenever the visitor might be
     leaving: panel close, tab hidden, page unload. sendBeacon survives page
     navigation. The Worker dedupes per session, so repeat beacons are safe
     and each just refreshes the stored transcript. Fires regardless of
     whether a lead was captured, so every conversation is reviewable. */
  var LOG_URL = PROXY_URL + '/log';
  var lastLoggedCount = 0;
  var convStartedAt = null;
  function beaconTranscript() {
    if (!conversationStarted || conversationHistory.length === 0) return;
    if (conversationHistory.length === lastLoggedCount) return; /* nothing new */
    if (!convStartedAt) convStartedAt = new Date().toISOString();
    lastLoggedCount = conversationHistory.length;
    var payload = JSON.stringify({
      sessionId: SESSION_ID,
      page: window.location.pathname,
      startedAt: convStartedAt,
      device: DEVICE,
      messages: conversationHistory
    });
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(LOG_URL, payload);
      } else {
        fetch(LOG_URL, { method: 'POST', body: payload, keepalive: true });
      }
    } catch (e) {}
  }
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'hidden') beaconTranscript();
  });
  window.addEventListener('pagehide', beaconTranscript);

  /* Keyboard open and close, and the iOS address bar collapsing on scroll,
     all surface as visualViewport changes. */
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncKeyboardInset);
    /* offsetTop changes on scroll rather than resize, and the fallback
       path above reads it. On a browser that resizes the layout viewport
       this fires rarely and computes a no-op, so it costs nothing there. */
    window.visualViewport.addEventListener('scroll', syncKeyboardInset);
  }
  window.addEventListener('orientationchange', function () {
    setTimeout(syncKeyboardInset, 300);
  });

  /* Any field inside the panel scrolls itself into view on focus, and the
     chat panel re-measures once the keyboard has settled. */
  document.addEventListener('focusin', function (e) {
    var panel = document.getElementById('nadia-panel');
    if (!panel || !panel.classList.contains('open')) return;
    if (!panel.contains(e.target)) return;
    var tag = (e.target.tagName || '').toLowerCase();
    if (tag !== 'input' && tag !== 'select' && tag !== 'textarea') return;
    panel.classList.add('nadia-kb-open');
    syncKeyboardInset();
    keepFocusVisible(e.target);
  });
  document.addEventListener('focusout', function () {
    /* Wait a beat before deciding the keyboard has gone. Tapping SEND blurs
       the input for an instant before focus returns to it, and without this
       the buttons would flicker back in and straight out again on every
       message sent. */
    setTimeout(function () {
      var panel = document.getElementById('nadia-panel');
      if (!panel) return;
      var active = document.activeElement;
      var stillTyping = active && panel.contains(active) &&
        ['input', 'select', 'textarea'].indexOf(
          (active.tagName || '').toLowerCase()) !== -1;
      if (!stillTyping) panel.classList.remove('nadia-kb-open');
      syncKeyboardInset();
    }, 150);
  });


  function closePanel() {
    var panel = document.getElementById('nadia-panel');
    panel.classList.remove('open');
    panel.classList.remove('nadia-kb-open');
    document.body.classList.remove('nadia-panel-open');
    useKeyboardResizingViewport(false);
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    try { window.scrollTo(0, scrollLockY); } catch (e) {}
    /* Drop any inline sizing so the stylesheet governs on next open. The
       bottom line also clears leftovers from the 18 Aug build, which sized
       the panel against visualViewport with a bottom offset. */
    panel.style.height = '';
    panel.style.transform = '';
    panel.style.bottom = '';
    document.getElementById('nadia-launcher').style.display = 'block';
    saveState();
    beaconTranscript();
    /* Tell the mobile bottom bar to come back. On mobile the original
       launcher above is visually clipped, so the bar is the only way
       back into the chat. */
    try { document.dispatchEvent(new CustomEvent('nadia:close')); } catch (e) {}
  }
  function dismissLauncher(e) {
    e.stopPropagation();
    hideNudge();
    document.getElementById('nadia-launcher').style.display = 'none';
  }

  /* Single entry point for any user-sent text (typed or endpoint button).
     If Nadia is mid-reply, queue it; finishTurn() will deliver it. The user's
     message always shows in the thread immediately so it never feels dropped. */
  function submitUserText(text) {
    text = (text || '').trim();
    if (!text) return;
    if (humanMode) {
      sendRelay(text);
      return;
    }
    addUserMessage(text);
    if (isStreaming) {
      pendingMessages.push(text);
    } else {
      streamMessage(text, false);
    }
  }

  function wireEvents() {
    document.getElementById('nadia-bubble').addEventListener('click', openPanel);
    document.getElementById('nadia-bubble-close').addEventListener('click', dismissLauncher);
    document.getElementById('nadia-nudge').addEventListener('click', function (e) {
      /* The close button dismisses only the card and leaves the launcher
         bubble in place, so a visitor who is not ready now can still come
         back to it. Clicking anywhere else on the card opens the chat. */
      if (e.target.closest('.nadia-nudge-close')) return;
      hideNudge(); openPanel();
    });
    document.getElementById('nadia-nudge-close').addEventListener('click', function (e) {
      e.stopPropagation();
      hideNudge();
    });
    document.getElementById('nadia-header-close').addEventListener('click', closePanel);

    document.getElementById('nadia-send').addEventListener('click', function() {
      var input = document.getElementById('nadia-input');
      var text = input.value.trim();
      if (!text) return;
      input.value = '';
      submitUserText(text);
    });

    document.getElementById('nadia-input').addEventListener('keydown', function(e) {
      if (e.key === 'Enter') document.getElementById('nadia-send').click();
    });

    var epMessages = {
      buying_furniture: 'I am looking to buy furniture soon and would like someone to call me.',
      buying_other: 'I am looking to buy something else soon and would like someone to call me.',
      find: 'I would like help finding the right sofa.',
      samples: 'I would like to order free fabric samples.',
      catalogue: 'Please send me the catalogue.',
      ask: 'I have a question.'
    };
    /* "I'm looking to buy soon, call me" opens a two-way split so the lead
       reaches the right team: furniture goes to sales (Freshsales), anything
       else to support (Gorgias). The choice is remembered in callbackKind and
       sent to the worker on every turn; the worker decides the queue from it. */
    function showBuyingChoice() {
      var c = document.getElementById('nadia-entry-points');
      if (!c) return;
      c.innerHTML =
        '<button class="nadia-ep-btn nadia-ep-primary" data-ep="buying_furniture">I\'m looking to buy furniture</button>' +
        '<button class="nadia-ep-btn nadia-ep-primary" data-ep="buying_other">I\'m looking to buy something else</button>';
    }
    /* Delegated so the dynamically added sub-choice buttons are handled too. */
    var epContainer = document.getElementById('nadia-entry-points');
    if (epContainer) {
      epContainer.addEventListener('click', function (e) {
        var btn = e.target.closest('.nadia-ep-btn');
        if (!btn) return;
        var ep = btn.dataset.ep;
        if (ep === 'buying') { showBuyingChoice(); return; }
        if (ep === 'buying_furniture' || ep === 'buying_other') {
          callbackKind = (ep === 'buying_other') ? 'other' : 'furniture';
          saveState();
          submitUserText(epMessages[ep]);
          epContainer.style.display = 'none';
          return;
        }
        var text = epMessages[ep];
        if (text) submitUserText(text);
      });
    }
  }

  // ============== INIT ==============
  function init() {
    if (document.getElementById('nadia-launcher')) {
      log('Nadia already initialised, skipping.');
      return;
    }
    signals = detectSignals();
    injectStyles();
    buildUI();
    wireEvents();
    log('Nadia ready. Click the bubble to start.');
    /* If the panel was open before a page navigation, reopen it. */
    try {
      if (sessionStorage.getItem('nadia_panel_open') === '1') {
        openPanel();
      } else {
        showNudge();
      }
    } catch (e) {}
  }

  /* Public API so the theme can open Nadia from existing section hooks
     (the fit-checker link, the intro button, etc.) and so the old scripted
     Nadia's launcher can be suppressed in favour of this real one. */
  window.SCNadia = {
    open: function() {
      if (!document.getElementById('nadia-launcher')) init();
      openPanel();
    },
    close: closePanel,
    isReady: function() { return !!document.getElementById('nadia-launcher'); }
  };

  /* On mobile (viewport <= 768px), delay Nadia's appearance by 60 seconds
     so the catalogue request CTA gets clear airtime. On desktop, show immediately. */
  function scheduleInit() {
    var isMobile = window.innerWidth <= 768;
    if (isMobile) {
      setTimeout(init, 60000);
    } else {
      init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit);
  } else {
    scheduleInit();
  }
})();
